import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { showToastMessage } from 'enable-ui';
import { useFormik } from 'formik';
import { institutionDetailSelector, useStationStore } from 'stores/station';

import {
  UserProfile,
  authenSelector,
  updateUserProfile,
  useAuthStore,
  userProfileSelector,
} from 'modules/auth/core';
import { RoleKey } from 'modules/auth/core/enum';

import { hasEmptyValues } from 'helpers';
import { apiCall } from 'hooks/service';
import { isEqual } from 'lodash';

import { PATH } from 'constants/path';

import { defaultInstitutionInfo } from '../constant';
import { getInstitutionDetail } from './helper';

export const useGeneralController = () => {
  const location = useLocation();

  const stations = useStationStore().state.stations;

  const userProfile = useAuthStore(userProfileSelector);

  const authen = useAuthStore(authenSelector);

  const { token } = authen;

  const roleKey = userProfile?.role?.key || '';

  const isBiologicAdmin = roleKey === RoleKey.BIOLOGIC_ADMIN;

  const institutionDetail = useStationStore(institutionDetailSelector);

  const [selectedStation, setSelectedStation] = useState<{ label: string; value: string } | null>(
    null,
  );

  const institutionInfo = {
    name: userProfile?.institution?.name,
    code: userProfile?.institution?.code || '',
  };

  const isInstitutionDetail = location.pathname.includes(`${PATH.INSTITUTIONS}`);

  const findSelectedStation = (id: string) => stations.find((station) => station.id === id);

  useEffect(() => {
    const station_id = isInstitutionDetail
      ? institutionDetail?.station_id
      : userProfile?.institution?.station_id;

    if (!station_id) {
      setSelectedStation(null);

      return;
    }

    const selected = findSelectedStation(station_id || '');

    if (selected) {
      setSelectedStation({
        label: selected?.name || '',
        value: selected?.id || '',
      });
    }
  }, [
    JSON.stringify(stations),
    userProfile,
    isInstitutionDetail,
    JSON.stringify(institutionDetail),
  ]);

  useEffect(() => {
    if (isInstitutionDetail) {
      setValues({
        name: institutionDetail?.name || '',
        code: institutionDetail?.code || '',
      });
    }

    if (!userProfile?.institution?.id) {
      return;
    }

    setValues(institutionInfo);
  }, [userProfile, isInstitutionDetail, JSON.stringify(institutionDetail)]);

  const getUserProfile = async () => {
    const result: UserProfile = await apiCall({
      url: `/auth/me`,
      method: 'GET',
      data: {
        token,
      },
      isLoading: true,
      showError: true,
    });

    if (result?.id) {
      updateUserProfile(result);
    }
  };

  const formik = useFormik({
    initialValues: defaultInstitutionInfo,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);

      if (!userProfile?.institution?.id && !institutionDetail?.id) {
        return;
      }

      const institutionId = isInstitutionDetail
        ? institutionDetail?.id
        : userProfile?.institution?.id;

      const headquarterAddressId = isInstitutionDetail
        ? institutionDetail?.headquarterAddress?.id
        : userProfile?.institution?.headquarterAddress?.id;

      const result: { id: string } = await apiCall({
        url: `/institutions/${institutionId}`,
        method: 'PATCH',
        data: {
          name: values.name,
          ...(isBiologicAdmin ? { code: values.code } : {}),
          stationId: selectedStation?.value,
        },
        isLoading: true,
        showError: true,
      });

      if (result?.id) {
        showToastMessage('success', 'Updated institution info successfully!');
      }

      if (institutionDetail) {
        getInstitutionDetail(institutionDetail.id);

        return;
      }

      getUserProfile();
    },
  });

  const { values, isSubmitting, isValid, handleSubmit, handleChange, setValues } = formik;

  const checkDiffValues = isInstitutionDetail
    ? isEqual(institutionDetail?.station_id ?? '', selectedStation?.value ?? '') &&
      isEqual(values.name, institutionDetail?.name) &&
      isEqual(values.code, institutionDetail?.code)
    : isEqual(userProfile?.institution?.station_id ?? '', selectedStation?.value ?? '') &&
      isEqual(values.name, institutionInfo.name);

  const disabled =
    hasEmptyValues({ name: values.name }) || isSubmitting || !isValid || checkDiffValues;

  const onCancel = () => {
    const stationId = isInstitutionDetail
      ? institutionDetail?.station_id
      : userProfile?.institution?.station_id;

    const selected = findSelectedStation(stationId || '');

    setSelectedStation({
      label: selected?.name || '',
      value: selected?.id || '',
    });

    if (isInstitutionDetail) {
      setValues({
        name: institutionDetail?.name || '',
        code: institutionDetail?.code || '',
      });

      return;
    }

    setValues(institutionInfo);
  };

  const handleStationsChange = (item: { label: string; value: string }) => {
    setSelectedStation(item);
  };

  const selectedStationInfo = useMemo(() => {
    const selected = findSelectedStation(selectedStation?.value || '');

    return {
      name: selected?.name || '',
      address: selected?.address || '',
      city: selected?.city || '',
      country: selected?.country || '',
      state: selected?.state || '',
      stationId: selected?.stationId || '',
      postalCode: selected?.postalCode || '',
    };
  }, [selectedStation?.value, JSON.stringify(stations)]);

  const mappingStations = useMemo(
    () =>
      stations.map((station) => ({
        label: station.name,
        value: station.id,
      })),
    [stations],
  );

  return {
    values,
    handleChange,
    handleSubmit,
    mappingStations,
    disabled,
    onCancel,
    isBiologicAdmin,
    handleStationsChange,
    selectedStation,
    selectedStationInfo,
  };
};
